// Libs
import classNames from 'classnames/bind';
import {
  Flex,
  IconButton,
  IconDiscussionReply2Line,
  IconEditLine,
  IconMoreLine,
  IconTrashLine,
  Menu,
  Text,
  View,
} from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
// Components, Layouts, Pages
import ReplyInput from '../ReplyInput/ReplyInput';
// Others
import { IDiscussionEntry } from '~/utils/interface/discussion';
import { formatDateTimeLabel, getFileIconByMimetype } from '~/utils/helper/common';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { deleteEntry } from '~/thunks/discussion/discussionThunk';
import { StorageEnum } from '~/utils/enum/base';
// Styles, images, icons
import styles from './DiscussionEntry.module.scss';

type Props = {
  courseId?: string;
  discussionId?: string;
  data: IDiscussionEntry;
};

const cx = classNames.bind(styles);

const DiscussionEntry = (props: Props) => {
  //#region Destructuring Props
  const { courseId, discussionId, data } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/discussion', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const { discussionDetail } = useAppSelector((state) => state.discussionState);
  //#endregion Selector

  //#region Declare State
  const [isReply, setIsReply] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const canvasAccountId = localStorage.getItem(StorageEnum.CANVAS_ACCOUNT_ID);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleEdit = () => {
    setIsEdit(true);
  };

  const handleDelete = () => {
    const isConfirmed = window.confirm(t('discussion_delete_confirm_message'));
    if (isConfirmed) {
      dispatch(
        deleteEntry({
          courseId,
          discussionId,
          entryId: data.id,
        })
      ).unwrap();
      return;
    }
  };

  const renderOptionMenu = () => {
    return (
      <Menu
        placement='bottom'
        trigger={
          <IconButton
            withBorder={false}
            withBackground={false}
            color='secondary'
            onClick={() => {}}
            screenReaderLabel={'More'}
          >
            {<IconMoreLine />}
          </IconButton>
        }
        mountNode={() => document.getElementById('main')}
      >
        <Menu.Item onClick={handleEdit}>
          <Flex gap='small'>
            <IconEditLine />
            <Text>{t('cancel_edit_label', { ns: 'common/base' })}</Text>
          </Flex>
        </Menu.Item>
        <Menu.Item onClick={handleDelete}>
          <Flex gap='small'>
            <IconTrashLine />
            <Text>{t('cancel_delete_label', { ns: 'common/base' })}</Text>
          </Flex>
        </Menu.Item>
      </Menu>
    );
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      <Flex gap='small' alignItems='start' justifyItems='space-between'>
        <Flex gap='small'>
          <img src={data.user?.avatarUrl} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('avatar')} />
          <Flex direction='column' gap='xx-small'>
            <Text size='small'>{data.user?.name}</Text>
            <Text size='x-small'>{formatDateTimeLabel(data.createdAt, t('discussion_at_time_label'))}</Text>
          </Flex>
        </Flex>

        {Number(data.user?.id) === Number(canvasAccountId) &&
          discussionDetail?.permissions?.reply &&
          renderOptionMenu()}
      </Flex>

      {isEdit ? (
        <View margin='medium 0 0 0'>
          <ReplyInput
            courseId={courseId}
            discussionId={discussionId}
            entryId={data.id}
            showCancelButton={true}
            data={data}
            onCancel={() => setIsEdit(!isEdit)}
          />
        </View>
      ) : (
        <>
          {data.message && <div dangerouslySetInnerHTML={{ __html: data.message }} />}

          {data?.attachments && data?.attachments?.length > 0 && (
            <div className={cx('attachmentContainer')}>
              {data.attachments.map((attachment, index) => {
                const FileIcon = getFileIconByMimetype(attachment.mimetype);
                return (
                  <div key={index} className={cx('attachmentItem')}>
                    <FileIcon />
                    <a href={attachment.url}>{attachment.displayName}</a>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {discussionDetail?.permissions?.reply && (
        <div className={cx('replyContainer')}>
          {isReply ? (
            <ReplyInput
              courseId={courseId}
              discussionId={discussionId}
              entryId={data.id}
              showCancelButton={true}
              onCancel={() => setIsReply(!isReply)}
            />
          ) : (
            <div className={cx('replyButton')} onClick={() => setIsReply(!isReply)}>
              <IconDiscussionReply2Line />
              <Text size='contentSmall'>{t('discussion_reply_button')}</Text>
            </div>
          )}
        </div>
      )}

      {data.replies && (
        <div className={cx('recentCommentContainer')}>
          {data.replies.map((recentReply, index) => (
            <DiscussionEntry key={index} courseId={courseId} discussionId={discussionId} data={recentReply} />
          ))}
        </div>
      )}
    </div>
  );
};

export default DiscussionEntry;
